import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { CheckCircle, Shield } from 'lucide-react';
import guestTranslations from '@/i18n/guestTranslations';

const SAFEBOX_CODE = '0426';

const GuestConfirmation = () => {
  const { apartment } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');
  const [lang] = useState(() => {
    const stored = sessionStorage.getItem('guest_lang');
    return stored || 'en';
  });
  const t = guestTranslations[lang] || guestTranslations.en;

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL || '';
    const poll = async () => {
      if (cancelled || attempts >= 8) { if (!cancelled) setStatus('error'); return; }
      try {
        const res = await fetch(`${API}/api/guest/checkout-status/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.status === 'expired') { setStatus('error'); return; }
        attempts++;
        setTimeout(poll, 2000);
      } catch { if (!cancelled) { attempts++; setTimeout(poll, 2000); } }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="confirmation-checking">
        <meta name="robots" content="noindex, nofollow" />
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#65c3ba] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#0A1312] text-lg">{t.confirmCheckingPayment}</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <meta name="robots" content="noindex, nofollow" />
        <p className="text-red-500 text-lg">Payment verification failed. Please contact support.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="confirmation-page">
      <meta name="robots" content="noindex, nofollow" />
      {/* Success header */}
      <div className="bg-[#65c3ba] text-white py-8 px-4 text-center">
        <CheckCircle className="mx-auto mb-3" size={48} />
        <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.confirmTitle}</h1>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Safebox Code */}
        <div className="bg-[#F7F9F9] border-2 border-[#65c3ba] rounded-sm p-8 text-center mb-8" data-testid="safebox-section">
          <Shield className="mx-auto mb-3 text-[#65c3ba]" size={32} />
          <p className="text-[#0A1312] text-lg mb-4">{t.safebox}</p>
          <div className="bg-[#3e716c] text-white rounded-sm py-6 px-8 mb-4" data-testid="safebox-code">
            <span className="text-6xl sm:text-8xl font-bold tracking-[0.3em]" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{SAFEBOX_CODE}</span>
          </div>
          <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4 mt-4">
            <p className="text-[#0A1312] text-sm font-semibold">{t.saveCode}</p>
          </div>
        </div>

        {/* Self check-in instructions */}
        <div className="border border-[#E2E8F0] rounded-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-[#0A1312] mb-3" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.selfCheckin}</h2>
          <p className="text-[#0A1312] leading-relaxed">{t.selfCheckinText}</p>
        </div>

        <div className="bg-[#F7F9F9] border border-[#E2E8F0] rounded-sm p-6">
          <p className="text-[#0A1312] leading-relaxed">{t.instructions}</p>
        </div>
      </div>
    </div>
  );
};

export default GuestConfirmation;
